import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Loading from "../../components/Buttons/Loading";
import useApi from "../../hooks/useApi";
import { updatePassword } from "../../api/FlaskAPI";
import "./styles.css";


const LockIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="var(--primary-dark, #355e7e)" strokeWidth="1.5" fill="none" />
    <path d="M8 11V7a4 4 0 1 1 8 0v4" stroke="var(--primary-dark, #355e7e)" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="16" r="1.5" fill="var(--primary-dark, #355e7e)" />
  </svg>
);

const SuccessIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#4caf50" />
    <path d="M8 12.5l2.5 2.5L16 9.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L1 21h22L12 2z" fill="#ff9800" />
    <line x1="12" y1="9" x2="12" y2="15" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="18" r="1" fill="white" />
  </svg>
);


function ResetPass() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { reset_token } = useParams();
  const resetRequest = useApi(updatePassword);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    errors: {},
  });

  // Status: form | success | expired
  const [status, setStatus] = useState("form");
  const [errorMessage, setErrorMessage] = useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    let errors = { ...formData.errors };

    if (name === "password") {
      if (!value) {
        errors.password = t("Sign.password_required");
      } else {
        delete errors.password;
      }
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        errors.confirmPassword = t("Sign.passwords_match_required");
      } else if (formData.confirmPassword) {
        delete errors.confirmPassword;
      }
    }

    if (name === "confirmPassword") {
      if (!value) {
        errors.confirmPassword = t("Sign.confirm_required");
      } else if (formData.password && value !== formData.password) {
        errors.confirmPassword = t("Sign.passwords_match_required");
      } else {
        delete errors.confirmPassword;
      }
    }

    setFormData({ ...formData, [name]: value, errors });
  };

  const handleReset = async () => {
    const { password, confirmPassword } = formData;

    if (!password || !confirmPassword) {
      setFormData({
        ...formData,
        errors: {
          password: !password ? t("Sign.password_required") : "",
          confirmPassword: !confirmPassword ? t("Sign.confirm_required") : "",
        },
      });
      return;
    }

    setFormData({ ...formData, errors: {} });

    if (password !== confirmPassword) {
      setFormData({
        ...formData,
        errors: {
          confirmPassword: t("Sign.passwords_match_required"),
        },
      });
      return;
    }

    try {
      const res = await resetRequest.request(
        { password, token: reset_token },
        false
      );

      if (res?.status === 200) {
        setStatus("success");
        const message =
          i18n.language === "ar" ? res.data?.message_ar : res.data?.message;
        toast.success(message || t("Sign.reset_success"));
        setTimeout(() => navigate("/account"), 3000);
      } else {
        handleError(res);
      }
    } catch (error) {
      handleError(error.response);
    }
  };

  const handleError = (res) => {
    setStatus("expired");
    const message =
      i18n.language === "ar" ? res?.data?.message_ar : res?.data?.message;
    setErrorMessage(message || t("Sign.link_expired"));
  };

  const goToForgotPassword = () => {
    navigate("/account", { state: { formType: "PASS_RESET" } });
  };

  return (
    <div className="reset-page">
      <div className="reset-card">
        {/* FORM STATE */}
        {status === "form" && (
          <>
            <div className="reset-icon">
              <LockIcon />
            </div>
            <h2 className="reset-title">{t("Sign.reset_password")}</h2>
            <p className="reset-subtitle">{t("Sign.enter_new_password")}</p>

            <div className="reset-field">
              <input
                name="password"
                type="password"
                value={formData.password}
                onChange={onChange}
                placeholder={t("Sign.password")}
                className={`reset-input ${formData.errors?.password ? "input-error" : ""}`}
              />
              {formData.errors?.password && (
                <span className="field-error">{formData.errors.password}</span>
              )}
            </div>

            <div className="reset-field">
              <input
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={onChange}
                placeholder={t("Sign.confirm_password")}
                className={`reset-input ${formData.errors?.confirmPassword ? "input-error" : ""}`}
              />
              {formData.errors?.confirmPassword && (
                <span className="field-error">{formData.errors.confirmPassword}</span>
              )}
            </div>

            {resetRequest.loading ? (
              <Loading />
            ) : (
              <button className="reset-btn primary" onClick={handleReset}>
                {t("Sign.reset_password")}
              </button>
            )}

            <a className="back-link" onClick={() => navigate("/account")}>
              {t("Sign.back_to_login")}
            </a>
          </>
        )}

        {/* SUCCESS STATE */}
        {status === "success" && (
          <>
            <div className="status-badge success">
              <SuccessIcon />
            </div>
            <h2 className="reset-title">{t("Sign.reset_success")}</h2>
            <p className="reset-subtitle">{t("Sign.redirecting_to_login")}</p>
          </>
        )}

        {/* EXPIRED / ERROR STATE */}
        {status === "expired" && (
          <>
            <div className="status-badge warning">
              <WarningIcon />
            </div>
            <h2 className="reset-title">{t("Sign.link_expired")}</h2>
            <p className="reset-subtitle error-text">{errorMessage}</p>

            <button className="reset-btn primary" onClick={goToForgotPassword}>
              {t("Sign.request_new_link")}
            </button>
            <a className="back-link" onClick={() => navigate("/account")}>
              {t("Sign.back_to_login")}
            </a>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPass;
